<template>
  <div v-if="recommendations.length > 0">
    <!-- Loop through recommendations -->
    <div v-for="(pathway, index) in recommendations" :key="pathway.pathwayId">
      <!-- Main Content: Image and Text -->
      <v-row no-gutters class="mb-2 mb-sm-4" style="min-height: 160px">
        <!-- Image Column -->
        <v-col cols="12" sm="4" md="4" lg="4" xl="4" class="d-flex align-stretch">
          <v-img
            :src="pathway.imageUrl || defaultImage"
            alt="Pathway Image"
            class="rounded w-100"
            cover
            style="min-height: 140px"
          />
        </v-col>

        <!-- Text Column -->
        <v-col
          cols="12"
          sm="8"
          md="8"
          lg="8"
          xl="8"
          class="ps-sm-4 pt-3 pt-sm-0 d-flex flex-column justify-space-between"
        >
          <!-- Top Content Group -->
          <div>
            <!-- Recommendation Badge -->
            <div class="mb-1">
              <span
                class="text-caption font-weight-bold"
                style="
                  background-color: #ffeebe;
                  color: #191919;
                  letter-spacing: 0.5px;
                  padding: 2px 10px;
                  border-radius: 4px;
                  display: inline-block;
                "
              >
                RECOMMENDED
              </span>
            </div>
            <h3 class="font-weight-bold mb-1 mt-0" style="font-size: 20px; line-height: 1.3">
              Pathway to {{ pathway.pathwayName }}
            </h3>

            <!-- Progress Bar (only show if user has progress) -->
            <div v-if="hasProgress(pathway)" class="mt-2">
              <ProgressBar
                :transcripted="getCompleted(pathway)"
                :waiting="0"
                :in-progress="getInProgress(pathway)"
                :not-started="getNotStarted(pathway)"
                :total-credits="getTotalCredits(pathway)"
                :show-legend="false"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <v-row no-gutters class="mt-4" align="center">
            <v-col cols="12" sm="auto" md="auto" lg="auto" xl="auto" class="mb-2 mb-sm-0 me-sm-2">
              <v-btn
                color="#ffc627"
                variant="flat"
                rounded
                class="text-none font-weight-bold px-6 mobile-touch-target w-100"
                size="default"
                style="color: #000000"
                :loading="isEnrolling(pathway.pathwayId)"
                :disabled="isEnrolling(pathway.pathwayId) || enrolledPathwayId === pathway.pathwayId"
                @click="handleEnrollPathway(pathway)"
              >
                <v-icon v-if="enrolledPathwayId === pathway.pathwayId" class="mr-2">mdi-check-circle</v-icon>
                {{ enrolledPathwayId === pathway.pathwayId ? 'Registered!' : 'Register for pathway' }}
              </v-btn>
            </v-col>
            <v-col cols="12" sm="auto" md="auto" lg="auto" xl="auto">
              <v-btn
                variant="outlined"
                rounded
                class="text-none font-weight-bold px-6 mobile-touch-target w-100"
                size="default"
                style="border-color: #2a2a2a; color: #2a2a2a"
                @click="handleViewDetails(pathway)"
              >
                View details
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Divider between recommendations (not after last one) -->
      <v-divider v-if="index < recommendations.length - 1" class="my-4" style="border-color: #bfbfbf; opacity: 1" />
    </div>

    <!-- Divider before Explore button -->
    <v-divider class="my-4" style="border-color: #bfbfbf; opacity: 1" />

    <!-- Explore Pathways Button -->
    <v-row no-gutters class="mt-4 justify-center">
      <v-col cols="12" sm="auto" md="auto" lg="auto" xl="auto">
        <v-btn
          color="#8c1d40"
          variant="flat"
          rounded
          class="text-none font-weight-bold px-6 mobile-touch-target w-100"
          size="default"
          style="color: #ffffff !important"
          @click="handleExplorePathways"
        >
          Explore more pathways
        </v-btn>
      </v-col>
    </v-row>
    <!-- Pathway Details Sidebar -->
    <pathway-details-sidebar
      v-model="showSidebar"
      :pathway-id="selectedPathway?.pathwayId"
      :pathway-data="pathwayDataForSidebar"
      @pathway-enrolled="handlePathwayEnrolled"
      @enroll-pathway="handleEnrollFromSidebar"
    />
  </div>
  <!-- Partner allows pathways but has none linked -->
  <p v-else-if="hasLoadedPathways" data-testid="pathways-empty-state" class="text-body-1 text-medium-emphasis mb-0">
    Pathways are currently unavailable.
  </p>
</template>

<script>
import { defineComponent } from 'vue';
import { usePathwaysStore } from '@/stores/pathways';
import { useUiStore } from '@/stores/ui';
import { DEFAULT_PATHWAY_IMAGE } from '@/constants/pathways';
import PathwayDetailsSidebar from './PathwayDetailsSidebar.vue';
import ProgressBar from '@/components/ui/ProgressBar.vue';
import { Logger } from '@/mixins/logger';
import { DataLayerMixin } from '@/mixins/dataLayerMixin';

export default defineComponent({
  name: 'PathwayRecommendationCard',

  mixins: [Logger, DataLayerMixin],

  components: {
    PathwayDetailsSidebar,
    ProgressBar
  },

  data() {
    return {
      showSidebar: false,
      selectedPathway: null,
      enrollingPathwayId: null,
      enrolledPathwayId: null,
      defaultImage: DEFAULT_PATHWAY_IMAGE
    };
  },

  computed: {
    pathwaysStore() {
      return usePathwaysStore();
    },

    // Get pathway state from the store
    pathwayState() {
      return this.pathwaysStore.pathwayState;
    },

    // Get recommended pathways from the my-pathway API response
    recommendations() {
      return this.pathwaysStore.getRecommendedPathways || [];
    },

    // my-pathway data has arrived (null until the dashboard loads it)
    hasLoadedPathways() {
      return this.pathwaysStore.myPathway !== null;
    },

    // Get credits needed from the API response
    creditsNeeded() {
      return this.pathwaysStore.myPathway?.creditsNeeded || 24;
    },

    // Transform pathway data for the sidebar component (new format)
    pathwayDataForSidebar() {
      const pathway = this.selectedPathway;
      if (!pathway) return {};

      return {
        pathwayId: pathway.pathwayId,
        pathwayName: pathway.pathwayName,
        pathwayDescription: pathway.pathwayDescription,
        imageUrl: pathway.imageUrl,
        location: pathway.location,
        credits: pathway.credits,
        creditsNeeded: pathway.creditsNeeded || pathway.credits,
        minGPA: pathway.minGPA,
        admissionRequirements: pathway.admissionRequirements,
        // Pass courses object directly from API
        courses: pathway.courses || { required: [], recommended: [] }
      };
    }
  },

  methods: {
    // Check if pathway has any progress (using new nested creditCompletion structure)
    hasProgress(pathway) {
      if (!pathway.creditCompletion?.overall) return false;
      const overall = pathway.creditCompletion.overall;
      return (overall.completed || 0) > 0 || (overall.inProgress || 0) > 0;
    },

    // Get effective credits considering required credits limiter
    // Required credits act as a hard limiter - missing required credits cap max progress
    getEffectiveCredits(pathway) {
      const creditCompletion = pathway.creditCompletion;
      const overall = creditCompletion?.overall;
      const required = creditCompletion?.required;
      const totalCredits = overall?.creditsNeeded || this.creditsNeeded;

      if (!overall) {
        return { completed: 0, inProgress: 0, totalCredits };
      }

      let completed = overall.completed || 0;
      let inProgress = overall.inProgress || 0;

      // If there are required credits, they act as a hard limiter
      if (required && required.total > 0) {
        const missingRequired = Math.max(0, required.total - required.completed);
        const maxAchievable = totalCredits - missingRequired;

        if (completed >= maxAchievable) {
          completed = maxAchievable;
          inProgress = 0;
        } else if (completed + inProgress > maxAchievable) {
          inProgress = maxAchievable - completed;
        }
      }

      // Cap at totalCredits
      if (completed >= totalCredits) {
        completed = totalCredits;
        inProgress = 0;
      } else if (completed + inProgress > totalCredits) {
        inProgress = totalCredits - completed;
      }

      return { completed, inProgress, totalCredits };
    },

    // Get completed credits from the new structure
    getCompleted(pathway) {
      return this.getEffectiveCredits(pathway).completed;
    },

    // Get in-progress credits from the new structure
    getInProgress(pathway) {
      return this.getEffectiveCredits(pathway).inProgress;
    },

    // Get total credits needed from the new structure
    getTotalCredits(pathway) {
      return pathway.creditCompletion?.overall?.creditsNeeded || this.creditsNeeded;
    },

    // Calculate not started credits
    getNotStarted(pathway) {
      const { completed, inProgress, totalCredits } = this.getEffectiveCredits(pathway);
      return Math.max(0, totalCredits - completed - inProgress);
    },

    async handleEnrollPathway(pathway) {
      if (!pathway.pathwayId) {
        return;
      }

      if (this.enrollingPathwayId === pathway.pathwayId) {
        return; // Prevent double-click
      }

      // GA tracking for enroll button click
      this.asuoDataLayerClick('PathwayRecommendationCard', '0');

      const uiStore = useUiStore();

      try {
        this.enrollingPathwayId = pathway.pathwayId;
        this.selectedPathway = pathway;
        uiStore.setLoadingState('enrolling');

        // Get user's contactId or asurite from connector service
        const contactId = this.$conn.learnerData?._contactId;
        const asurite = this.$conn.learnerData?._asuRite;

        // Prefer contactId, fallback to asurite
        const identifier = contactId ? { contactId } : asurite ? { asuRite: asurite } : null;

        if (!identifier) {
          throw new Error('User identifier (contactId or asuRite) not found');
        }

        // Call the pathway enrollment API
        await this.$conn.updatePathwayConnection(identifier, {
          pathwayId: pathway.pathwayId
        });

        // Set success state
        this.enrolledPathwayId = pathway.pathwayId;
        this.enrollingPathwayId = null;

        // Show success notification
        uiStore.showSuccessNotification(`Pathway to ${pathway.pathwayName}`, 'has been added to your dashboard.');

        uiStore.setLoadingState(null);

        // Emit event to parent (Dashboard) to refresh pathway data
        // This avoids a full page reload and provides better UX
        setTimeout(() => {
          this.$emit('pathway-enrolled');
        }, 1500);
      } catch (error) {
        this.logError('Failed to enroll in pathway', error);

        // Check for specific error messages from the backend
        const errorMessage = error?.response?.data?.message || error?.message || '';

        if (errorMessage.includes('not authorized to enroll') || errorMessage.includes('Partner is not authorized')) {
          uiStore.showErrorNotification(
            'Pathway Not Available',
            'This pathway is not available for your account. Please choose a different pathway.'
          );
        } else {
          uiStore.showErrorNotification('Enrollment Failed', 'Failed to enroll in pathway. Please try again.');
        }
        this.enrollingPathwayId = null;
        uiStore.setLoadingState(null);
      }
    },

    handleViewDetails(pathway) {
      // GA tracking for view details button click
      this.asuoDataLayerClick('PathwayRecommendationCard', '1');
      this.selectedPathway = pathway;
      this.showSidebar = true;
    },

    handleExplorePathways() {
      // Navigate to pathways page and scroll to degree options
      this.$router.push({ path: '/pathways', query: { scrollTo: 'degrees' } });
    },

    handlePathwayEnrolled() {
      // Emit event to parent (Dashboard) to refresh
      this.$emit('pathway-enrolled');
      // Close sidebar
      this.showSidebar = false;
    },

    handleEnrollFromSidebar() {
      if (this.selectedPathway) {
        this.handleEnrollPathway(this.selectedPathway);
      }
    },

    isEnrolling(pathwayId) {
      return this.enrollingPathwayId === pathwayId;
    }
  }
});
</script>

<style scoped>
/* Minimal custom styles - prefer utility classes */
</style>
